import type {
  TApplicant,
  TApplicantRole,
  TApplicantSortColumn,
  TApplicantStatus,
  TVerificationOutcome,
} from '@/services/applicants';
import type { TPermission } from '@/constants/permissions';

type TApplicantChipVariant = 'success' | 'warning' | 'info' | 'danger' | 'neutral' | 'zero' | 'single-pink' | 'client-teal';

type TApplicantColumn =
  | 'applicant'
  | 'member'
  | 'type'
  | 'stage'
  | 'owner'
  | 'applied'
  | 'since'
  | 'source'
  | 'location'
  | 'age'
  | 'market'
  | 'result'
  | 'identity'
  | 'session'
  | 'outcome'
  | 'notes'
  | 'actions';

type TApplicantCheckResult = 'serious_offense' | 'failed';

type TApplicantColumnSpec = {
  key: TApplicantColumn;
  width: string;
};

type TApplicantRowActionID = 'advance' | 'approve' | 'archive' | 'pass' | 'review';

type TApplicantRowAction = {
  id: TApplicantRowActionID;
  label: string;
  variant: 'primary' | 'secondary' | 'neutral' | 'destructive-outline' | 'ghost';
  isDisabled?: boolean;
};

type TApplicantTileID = 'in-progress' | 'cleared-week' | 'flagged';

type TApplicantTileSpec = {
  id: TApplicantTileID;
  label: string;
};

type TApplicantTab = {
  value: string;
  label: string;
  count?: number;
};

type TApplicantFilterOption = {
  value: string;
  label: string;
};

type TApplicantFilterSpec = {
  id: string;
  placeholder: string;
  width: string;
  options: readonly TApplicantFilterOption[];
};

type TApplicantPermission = {
  permission: string;
  holders: readonly string[];
};

type TApplicantStageTab = {
  value: string;
  label: string;
  statuses: readonly TApplicantStatus[];
  isInterviewPast?: boolean;
  isArchived?: boolean;
  permission?: TPermission;
};

type TApplicantsView = {
  title: string;
  subtitle: string;
  searchPlaceholder: string;
  statuses: readonly TApplicantStatus[];
  columns: readonly TApplicantColumnSpec[];
  filters?: readonly TApplicantFilterSpec[];
  countNoun?: { one: string; many: string };
  countSuffix?: string;
  permission?: TApplicantPermission;
  segments?: readonly TApplicantSegment[];
  tabs?: readonly TApplicantTab[];
  actions?: readonly TApplicantRowAction[];
  total?: number;
};

type TApplicantRow = {
  applicant: TApplicant;
  ownerName: string | null;
  ownerOpenCount: number | null;
  invitationRef: string | null;
  inviterName?: string;
  subLine?: string;
  sourceLabel?: string;
  location?: string;
  age?: number;
  checkRef?: string;
  checkResult?: TApplicantCheckResult;
  note?: string;
  actionNote?: string;
  actions: readonly TApplicantRowAction[];
};

const APPLICANTS_COPY = {
  columnApplicant: 'Applicant',
  columnType: 'Type',
  columnStage: 'Stage',
  columnOwner: 'Owner',
  columnApplied: 'Applied',
  columnMember: 'Member / applicant',
  columnSince: 'Since',
  columnSource: 'Source',
  columnLocation: 'Location',
  columnAge: 'Age',
  countApplicantsOne: 'applicant',
  countApplicantsMany: 'applicants',
  columnResult: 'Result',
  columnNotes: 'Notes',
  columnActions: '',
  sourcesLabel: 'All sources',
  anyDateLabel: 'Any date',
  clearDateLabel: 'Any date',
  ownerUnassigned: 'Unassigned',
  ownerOpenSuffix: 'open',
  invitationPrefix: 'via',
  countOne: 'record',
  countMany: 'records',
  showing: 'Showing',
  of: 'of',
  blank: '—',
  columnMarket: 'Market',
  terminalNote: 'Terminal — no override exists',
  heldNote: 'Held in Further Review',
  columnIdentity: 'ID + liveness',
  columnSession: 'Session ref',
  allStatuses: 'All statuses',
  allTypes: 'All types',
  allStages: 'All stages',
  allAssignees: 'All assignees',
  flaggedNote: 'Decision on Background Check',
  emptyTitle: 'Nothing here yet.',
  emptyDescription: 'No applicant matches this view. Clear the search or try another term.',
  errorDescription: 'Nothing was loaded. Reload the page or try again in a moment.',
} as const;

const APPLICANT_COLUMN_LABEL: Record<TApplicantColumn, string> = {
  'applicant': APPLICANTS_COPY.columnApplicant,
  'type': APPLICANTS_COPY.columnType,
  'stage': APPLICANTS_COPY.columnStage,
  'owner': APPLICANTS_COPY.columnOwner,
  'applied': APPLICANTS_COPY.columnApplied,
  'member': APPLICANTS_COPY.columnMember,
  'since': APPLICANTS_COPY.columnSince,
  'source': APPLICANTS_COPY.columnSource,
  'location': APPLICANTS_COPY.columnLocation,
  'market': APPLICANTS_COPY.columnMarket,
  'age': APPLICANTS_COPY.columnAge,
  'result': APPLICANTS_COPY.columnResult,
  'identity': APPLICANTS_COPY.columnIdentity,
  'session': APPLICANTS_COPY.columnSession,
  'outcome': APPLICANTS_COPY.columnResult,
  'notes': APPLICANTS_COPY.columnNotes,
  'actions': APPLICANTS_COPY.columnActions,
};

const POOL_COLUMNS: readonly TApplicantColumnSpec[] = [
  { key: 'applicant', width: 'w-[490px]' },
  { key: 'type', width: 'w-[102px]' },
  { key: 'stage', width: 'w-[182px]' },
  { key: 'owner', width: 'w-[212px]' },
  { key: 'applied', width: 'w-[116px]' },
];

const ALL_APPLICANTS_COLUMNS: readonly TApplicantColumnSpec[] = [
  { key: 'applicant', width: 'w-[318px]' },
  { key: 'type', width: 'w-[102px]' },
  { key: 'stage', width: 'w-[192px]' },
  { key: 'owner', width: 'w-[162px]' },
  { key: 'applied', width: 'w-min whitespace-nowrap' },
  { key: 'actions', width: 'w-min whitespace-nowrap' },
];

const MY_APPLICANTS_COLUMNS: readonly TApplicantColumnSpec[] = [
  { key: 'applicant', width: 'w-[230px]' },
  { key: 'stage', width: 'w-[130px]' },
  { key: 'type', width: 'w-[90px]' },
  { key: 'location', width: 'w-[120px]' },
  { key: 'age', width: 'w-[50px]' },
  { key: 'applied', width: 'w-[90px]' },
  { key: 'source', width: 'w-[150px]' },
  { key: 'actions', width: 'w-[190px]' },
];

const ALL_APPLICANTS_ACTIONS: readonly TApplicantRowAction[] = [
  { id: 'advance', label: 'Open', variant: 'secondary' },
];

const MY_APPLICANTS_ACTIONS: readonly TApplicantRowAction[] = [
  { id: 'advance', label: 'Open', variant: 'primary' },
  { id: 'archive', label: 'Archive', variant: 'secondary' },
];

const ARCHIVED_ROW_ACTIONS: readonly TApplicantRowAction[] = [
  { id: 'advance', label: 'Open', variant: 'primary' },
];

const APPLICANT_ROW_ACTION_PERMISSION: Record<TApplicantRowActionID, TPermission> = {
  'advance': 'applicants:advance',
  'approve': 'boss-applicants:manage',
  'archive': 'applicants:archive',
  'pass': 'background-check:view',
  'review': 'background-check:view',
};

const APPLICANT_SEGMENTS: readonly TApplicantSegment[] = [
  { value: 'all', label: 'All' },
  { value: 'singles', label: 'Singles', roles: ['single'] },
  { value: 'clients', label: 'Clients', roles: ['client'] },
];

const ALL_APPLICANTS_TABS: readonly TApplicantStageTab[] = [
  { value: 'all', label: 'All', statuses: [] },
  { value: 'new', label: 'New', statuses: ['applied'] },
  { value: 'review', label: 'Review', statuses: ['recruiter_review'] },
  { value: 'interview', label: 'Interview', statuses: ['interview_invited', 'interview_scheduled'] },
  {
    value: 'scoring',
    label: 'Scoring',
    statuses: ['interview_scheduled'],
    isInterviewPast: true,
  },
  { value: 'checks', label: 'Checks', statuses: ['verification_pending'] },
  { value: 'final', label: 'Final approval', statuses: ['pending_final_approval'] },
  {
    value: 'archived',
    label: 'Archived',
    statuses: [],
    isArchived: true,
    permission: 'applicants:view-archived',
  },
];

const MY_APPLICANTS_OPEN_STATUSES: readonly TApplicantStatus[] = [
  'applied',
  'recruiter_review',
  'interview_invited',
  'interview_scheduled',
  'verification_pending',
  'pending_final_approval',
];

const MY_APPLICANTS_TABS: readonly TApplicantStageTab[] = [
  { value: 'all', label: 'All', statuses: [] },
  { value: 'new', label: 'New', statuses: ['applied'] },
  { value: 'review', label: 'Review', statuses: ['recruiter_review'] },
  { value: 'interview', label: 'Interview', statuses: ['interview_invited', 'interview_scheduled'] },
  {
    value: 'scoring',
    label: 'Scoring',
    statuses: ['interview_scheduled'],
    isInterviewPast: true,
  },
  { value: 'checks', label: 'Checks', statuses: ['verification_pending'] },
  { value: 'final', label: 'Final approval', statuses: ['pending_final_approval'] },
  { value: 'waitlist', label: 'Waitlist', statuses: ['approved_waitlisted'] },
  { value: 'activation', label: 'Activation', statuses: ['activation_ready'] },
  { value: 'rejected', label: 'Rejected', statuses: ['rejected'] },
  {
    value: 'archived',
    label: 'Archived',
    statuses: [],
    isArchived: true,
  },
];

const APPLICANT_COLUMN_SORT: Partial<Record<TApplicantColumn, TApplicantSortColumn>> = {
  'applicant': 'last_name',
  'stage': 'status',
  'applied': 'created_at',
};

const APPLICANT_SOURCE_CLASS_LABEL: Record<string, string> = {
  'referral_member': 'Member referral',
  'referral_team': 'Team referral',
  'agency_member_referral': 'Agency member referral',
  'qualified_lead_referral': 'Qualified lead referral',
  'referral_waitlist_cold': 'Waitlist referral',
  'organic_cold': 'Organic',
  'ad_cold': 'Ad campaign',
  'social_cold': 'Social',
};

const APPLICANT_FILTER_ALL = 'all';

const SOURCE_FILTER: TApplicantFilterSpec = {
  id: 'source',
  placeholder: APPLICANTS_COPY.sourcesLabel,
  width: 'w-[140px]',
  options: [
    { value: APPLICANT_FILTER_ALL, label: APPLICANTS_COPY.sourcesLabel },
    ...Object.entries(APPLICANT_SOURCE_CLASS_LABEL).map(([value, label]) => ({ value, label })),
  ],
};

const SOURCE_FILTERS: readonly TApplicantFilterSpec[] = [SOURCE_FILTER];

const GATE_RESULT_FILTER_ID = 'result';
const GATE_TYPE_FILTER_ID = 'type';
const GATE_STAGE_FILTER_ID = 'stage';
const GATE_ASSIGNEE_FILTER_ID = 'assignee';

const GATE_STATUSES: readonly TApplicantStatus[] = [
  'verification_pending',
  'pending_final_approval',
  'approved_waitlisted',
  'rejected',
];

const GATE_FILTERS: readonly TApplicantFilterSpec[] = [
  {
    id: GATE_RESULT_FILTER_ID,
    placeholder: APPLICANTS_COPY.allStatuses,
    width: 'w-[140px]',
    options: [
      { value: APPLICANT_FILTER_ALL, label: APPLICANTS_COPY.allStatuses },
      { value: 'clear', label: 'Passed' },
      { value: 'pending', label: 'Pending' },
      { value: 'flagged', label: 'Flagged' },
      { value: 'failed', label: 'Failed' },
    ],
  },
  {
    id: GATE_TYPE_FILTER_ID,
    placeholder: APPLICANTS_COPY.allTypes,
    width: 'w-[120px]',
    options: [
      { value: APPLICANT_FILTER_ALL, label: APPLICANTS_COPY.allTypes },
      { value: 'client', label: 'Client' },
      { value: 'single', label: 'Single' },
    ],
  },
  {
    id: GATE_STAGE_FILTER_ID,
    placeholder: APPLICANTS_COPY.allStages,
    width: 'w-[160px]',
    options: [
      { value: APPLICANT_FILTER_ALL, label: APPLICANTS_COPY.allStages },
      { value: 'verification_pending', label: 'Verification pending' },
      { value: 'pending_final_approval', label: 'Final approval' },
      { value: 'approved_waitlisted', label: 'Waitlisted' },
      { value: 'rejected', label: 'Rejected' },
    ],
  },
];

const GATE_ASSIGNEE_WIDTH = 'w-[160px]';

const GATE_TILES: readonly TApplicantTileSpec[] = [
  { id: 'in-progress', label: 'In progress' },
  { id: 'cleared-week', label: 'Cleared this week' },
  { id: 'flagged', label: 'Flagged' },
];

const GATE_OUTCOME_LABEL: Record<TVerificationOutcome, string> = {
  'clear': 'Passed',
  'pending': 'Pending',
  'flagged': 'Flagged',
  'failed': 'Failed',
  'expired': 'Expired',
};

const GATE_OUTCOME_VARIANT: Record<TVerificationOutcome, TApplicantChipVariant> = {
  'clear': 'success',
  'pending': 'warning',
  'flagged': 'warning',
  'failed': 'danger',
  'expired': 'warning',
};

const GATE_IDENTITY_LABEL: Record<TVerificationOutcome, string> = {
  'clear': 'Verified',
  'pending': 'Running',
  'flagged': 'Flagged',
  'failed': 'Failed',
  'expired': 'Expired',
};

const APPLIED_RANGE = {
  fromID: 'appliedFrom',
  toID: 'appliedTo',
  width: 'w-[220px]',
} as const;

const APPLIED_PRESETS = [
  { id: 'last-30', label: 'Last 30 days' },
  { id: 'last-60', label: 'Last 60 days' },
] as const;

const APPLICANT_ROLE_LABEL: Record<TApplicantRole, string> = {
  'client': 'Client',
  'single': 'Single',
  'staff': 'Staff',
};

const APPLICANT_ROLE_VARIANT: Record<TApplicantRole, TApplicantChipVariant> = {
  'client': 'client-teal',
  'single': 'single-pink',
  'staff': 'neutral',
};

const APPLICANT_STATUS_LABEL: Record<TApplicantStatus, string> = {
  'draft': 'Draft',
  'applied': 'Applied',
  'recruiter_review': 'Recruiter Review',
  'interview_invited': 'Interview Invited',
  'interview_scheduled': 'Interview Scheduled',
  'verification_pending': 'Verification Pending',
  'pending_final_approval': 'Final approval',
  'approved_waitlisted': 'Waitlisted',
  'activation_ready': 'Activation Ready',
  'active': 'Active',
  'paused': 'Paused',
  'rejected': 'Rejected',
  'churned': 'Churned',
  'barred': 'Barred',
};

const APPLICANT_CHECK_RESULT_LABEL: Record<TApplicantCheckResult, string> = {
  'serious_offense': 'Serious offense',
  'failed': 'Failed',
};

const APPLICANT_CHECK_RESULT_VARIANT: Record<TApplicantCheckResult, TApplicantChipVariant> = {
  'serious_offense': 'warning',
  'failed': 'danger',
};

const BOSS_APPLICANTS_COLUMNS: readonly TApplicantColumnSpec[] = [
  { key: 'applicant', width: 'w-[332px]' },
  { key: 'age', width: 'w-[70px]' },
  { key: 'market', width: 'w-[130px]' },
  { key: 'applied', width: 'w-[150px]' },
  { key: 'stage', width: 'w-[150px]' },
  { key: 'actions', width: 'w-[230px]' },
];

const BOSS_APPLICANTS_TABS: readonly TApplicantStageTab[] = [
  { value: 'pool', label: 'Pool', statuses: ['applied'] },
  { value: 'all', label: 'All Applicants', statuses: [] },
];

const BOSS_APPLICANTS_ACTIONS: readonly TApplicantRowAction[] = [
  { id: 'archive', label: 'Archive', variant: 'secondary' },
  { id: 'approve', label: 'Approve', variant: 'primary' },
];

const FAILED_CHECK_COLUMNS: readonly TApplicantColumnSpec[] = [
  { key: 'applicant', width: 'w-[419px]' },
  { key: 'type', width: 'w-[102px]' },
  { key: 'result', width: 'w-[162px]' },
  { key: 'notes', width: 'w-[419px]' },
];

const GATE_COLUMNS: readonly TApplicantColumnSpec[] = [
  { key: 'applicant', width: 'w-[362px]' },
  { key: 'type', width: 'w-[92px]' },
  { key: 'identity', width: 'w-[152px]' },
  { key: 'session', width: 'w-[132px]' },
  { key: 'outcome', width: 'w-[112px]' },
  { key: 'actions', width: 'w-auto' },
];

const ON_THE_APP_COLUMNS: readonly TApplicantColumnSpec[] = [
  { key: 'member', width: 'w-[702px]' },
  { key: 'type', width: 'w-[102px]' },
  { key: 'source', width: 'w-[142px]' },
  { key: 'since', width: 'w-[156px]' },
];

const APPLICANT_SORT_COLUMNS = ['created_at', 'last_name', 'status', 'interview_starts_at'] as const;

const APPLICANTS_PER_PAGE = 10;

type TApplicantOwnerKind = 'recruiter' | 'concierge';

type TApplicantListViewID =
  | 'new-applicants'
  | 'my-applicants'
  | 'all-applicants'
  | 'verified'
  | 'approved'
  | 'on-the-app'
  | 'failed-background-check'
  | 'background-check'
  | 'boss-applicants';

type TApplicantSegment = {
  value: string;
  label: string;
  roles?: readonly TApplicantRole[];
};

type TApplicantListCopy = {
  title: string;
  subtitle: string;
  emptyTitle: string;
  emptyDescription: string;
};

type TApplicantsListView = {
  title: string;
  subtitle: string;
  searchPlaceholder: string;
  emptyTitle: string;
  emptyDescription: string;
  viewAll?: TApplicantListCopy;
  columns: readonly TApplicantColumnSpec[];
  statuses?: readonly TApplicantStatus[];
  tabs?: readonly TApplicantStageTab[];
  segments?: readonly TApplicantSegment[];
  ownerKind?: TApplicantOwnerKind;
  isOwnedByRecruiter?: boolean;
  isTerminalLocked?: boolean;
  actions?: readonly TApplicantRowAction[];
  filters?: readonly TApplicantFilterSpec[];
  hasAppliedRange?: boolean;
  checkOutcomes?: readonly TVerificationOutcome[];
  permission?: TApplicantPermission;
  isGateView?: boolean;
  isRowOpenable?: boolean;
  hasAssigneeFilter?: boolean;
  tiles?: readonly TApplicantTileSpec[];
};

const APPLICANT_LIST_VIEWS: Record<TApplicantListViewID, TApplicantsListView> = {
  'new-applicants': {
    title: 'New Applicants Pool',
    subtitle: 'Applicants at the APPLIED stage — each already owned by the recruiter assigned at intake.',
    searchPlaceholder: 'Search new applicants pool',
    emptyTitle: 'The pool is empty.',
    emptyDescription: 'No applicant is sitting at the APPLIED stage right now.',
    filters: SOURCE_FILTERS,
    columns: POOL_COLUMNS,
    statuses: ['applied'],
  },
  'my-applicants': {
    title: 'My Applicants',
    subtitle: 'Your assigned recruits — auto-assigned at intake by round-robin.',
    searchPlaceholder: 'Search your applicants',
    emptyTitle: 'No applicants assigned to you yet.',
    emptyDescription: 'Applicants are auto-assigned at intake. Nothing has reached your queue.',
    columns: MY_APPLICANTS_COLUMNS,
    hasAppliedRange: true,
    tabs: MY_APPLICANTS_TABS,
    segments: APPLICANT_SEGMENTS,
    actions: MY_APPLICANTS_ACTIONS,
    isOwnedByRecruiter: true,
    viewAll: {
      title: 'My Applicants',
      subtitle: 'Every applicant in the pipeline — this role is not scoped to an owner.',
      emptyTitle: 'No applicants yet.',
      emptyDescription: 'Nothing has entered the pipeline, or the search matched no one.',
    },
  },
  'all-applicants': {
    title: 'All Applicants',
    subtitle: 'Every applicant, every stage — Recruiter Manager, Concierge Manager and Super Admin.',
    searchPlaceholder: 'Search all applicants',
    emptyTitle: 'No applicants yet.',
    emptyDescription: 'Nothing has entered the pipeline, or the search matched no one.',
    columns: ALL_APPLICANTS_COLUMNS,
    filters: SOURCE_FILTERS,
    tabs: ALL_APPLICANTS_TABS,
    segments: APPLICANT_SEGMENTS,
    actions: ALL_APPLICANTS_ACTIONS,
    permission: {
      permission: 'all_lists.view',
      holders: ['recruiter-manager (flag)', 'concierge-manager (flag)', 'super-admin'],
    },
  },
  'verified': {
    title: 'Verified',
    subtitle: 'Passed verification — awaiting Super-Admin final approval.',
    searchPlaceholder: 'Search verified',
    emptyTitle: 'Nobody is awaiting final approval.',
    emptyDescription: 'Applicants land here once verification comes back approved.',
    filters: SOURCE_FILTERS,
    columns: POOL_COLUMNS,
    statuses: ['pending_final_approval'],
    isRowOpenable: true,
  },
  'approved': {
    title: 'Approved',
    subtitle: 'Approved and waitlisted for activation.',
    searchPlaceholder: 'Search approved',
    emptyTitle: 'The waitlist is empty.',
    emptyDescription: 'Applicants land here after the Super-Admin final approval.',
    filters: SOURCE_FILTERS,
    columns: POOL_COLUMNS,
    statuses: ['approved_waitlisted'],
  },
  'on-the-app': {
    title: 'On the App',
    subtitle: 'Members and applicants who are live on the app.',
    searchPlaceholder: 'Search on the app',
    emptyTitle: 'Nobody is live on the app yet.',
    emptyDescription: 'Members appear here once activation completes.',
    columns: ON_THE_APP_COLUMNS,
    filters: SOURCE_FILTERS,
    statuses: ['active'],
    ownerKind: 'concierge',
  },
  'failed-background-check': {
    title: 'Failed Background Check',
    subtitle: 'Background checks that came back Failed — decisions live on Background Check.',
    searchPlaceholder: 'Search failed background check',
    emptyTitle: 'No failed checks.',
    emptyDescription: 'Nobody carries a terminal disqualification right now.',
    columns: FAILED_CHECK_COLUMNS,
    checkOutcomes: ['failed', 'flagged'],
  },
  'background-check': {
    title: 'Background Check',
    subtitle: 'Identity checks for applicants at the verification gate — results come back as outcomes.',
    searchPlaceholder: 'Search',
    emptyTitle: 'No checks match these filters.',
    emptyDescription: '',
    columns: GATE_COLUMNS,
    statuses: GATE_STATUSES,
    filters: GATE_FILTERS,
    hasAssigneeFilter: true,
    hasAppliedRange: true,
    isGateView: true,
    tiles: GATE_TILES,
    actions: [
      { id: 'pass', label: 'Pass', variant: 'secondary' },
      { id: 'review', label: 'Review', variant: 'secondary' },
    ],
  },
  'boss-applicants': {
    title: 'Applicants',
    subtitle: 'The intake queue — approve into the funnel, or archive out of it.',
    searchPlaceholder: 'Search applicants',
    emptyTitle: 'Nothing in the pool.',
    emptyDescription: 'No applicant is sitting at intake right now.',
    columns: BOSS_APPLICANTS_COLUMNS,
    tabs: BOSS_APPLICANTS_TABS,
    actions: BOSS_APPLICANTS_ACTIONS,
  },
};

export type {
  TApplicantCheckResult,
  TApplicantTileID,
  TApplicantTileSpec,
  TApplicantRowActionID,
  TApplicantSegment,
  TApplicantListCopy,
  TApplicantChipVariant,
  TApplicantColumn,
  TApplicantColumnSpec,
  TApplicantFilterSpec,
  TApplicantPermission,
  TApplicantListViewID,
  TApplicantOwnerKind,
  TApplicantRow,
  TApplicantRowAction,
  TApplicantStageTab,
  TApplicantTab,
  TApplicantsListView,
  TApplicantsView,
};
export {
  ARCHIVED_ROW_ACTIONS,
  GATE_ASSIGNEE_FILTER_ID,
  GATE_ASSIGNEE_WIDTH,
  GATE_IDENTITY_LABEL,
  GATE_OUTCOME_LABEL,
  GATE_OUTCOME_VARIANT,
  GATE_RESULT_FILTER_ID,
  GATE_STAGE_FILTER_ID,
  GATE_TYPE_FILTER_ID,
  APPLICANTS_COPY,
  APPLICANT_FILTER_ALL,
  APPLIED_PRESETS,
  APPLIED_RANGE,
  APPLICANT_ROW_ACTION_PERMISSION,
  APPLICANT_SEGMENTS,
  APPLICANTS_PER_PAGE,
  APPLICANT_CHECK_RESULT_LABEL,
  APPLICANT_CHECK_RESULT_VARIANT,
  APPLICANT_COLUMN_LABEL,
  APPLICANT_COLUMN_SORT,
  APPLICANT_LIST_VIEWS,
  APPLICANT_ROLE_LABEL,
  APPLICANT_ROLE_VARIANT,
  APPLICANT_SORT_COLUMNS,
  APPLICANT_SOURCE_CLASS_LABEL,
  APPLICANT_STATUS_LABEL,
  MY_APPLICANTS_OPEN_STATUSES,
  MY_APPLICANTS_TABS,
};
